/* eslint-disable @typescript-eslint/camelcase */

import React, { useState } from 'react';
import { Field } from 'formik';
import axios from 'axios';
import set from 'lodash/set';
import AsyncSelect from 'react-select/async';
import { v4 as uuidv4 } from 'uuid';
import { ValueType } from 'react-select';
import { MenuListComponentProps } from 'react-select/src/components/Menu';
import { isJwtExpired } from 'jwt-check-expiration';
import { isExperienceEditorActive } from '@sitecore-jss/sitecore-jss-react';

import config from 'jss-boilerplate/temp/config.js';
import { isStorybook } from 'stories/utils';
import {
	ReferenceDataSuperFunds,
	ReferenceDataSuperFundsDataValues,
} from 'core/api/client/ReferenceDataApi';
import { getAccessToken } from 'core/browserStorage';
import { doRefreshToken } from 'core/utils/api-utils';
import {
	AKAMAI_BOT_FLAG,
	ACTIVE_FUND_STATUS,
	X_CORRELATION_APP,
} from 'core/constants';
import GetSuperFundsApra from 'core/hooks/useGetReferences/api/mocks/GetSuperFundsApra.mock.json';
import GetSuperFundsUsi from 'core/hooks/useGetReferences/api/mocks/GetSuperFundsUsi.mock.json';
import GetSMSFYesAbnFunds from 'core/hooks/useGetReferences/api/mocks/GetSMSFYesAbnFunds.mock.json';
import { isJssField } from 'core/utils/sitecore-utils';
import IProps from 'pods/forms/components/formFieldProps.d';
import UpdatedCtrlHolder from 'pods/forms/components/structural/UpdatedCtrlHolder';

import {
	MANUAL_FUND_OPTION,
	MANUAL_LABEL,
	SB_FAIL,
} from './FundsAutocomplete.const';
import * as S from './FundsAutocomplete.style';

export type TOption = {
	label: string;
	value: string;
};

export type TReferenceDataSuperFundsDataValuesForUsi = Pick<
	ReferenceDataSuperFundsDataValues,
	'eRollover' | 'usi' | 'fund_code' | 'abn' | 'name'
>;

export type TReferenceDataSuperFundsUnion =
	| ReferenceDataSuperFundsDataValues
	| TReferenceDataSuperFundsDataValuesForUsi;

// API base config
const apiConfig = config;
const referenceBase = `${config.australianSuperApi?.apiDomain}${config.australianSuperApi?.apiPathReferenceData}`;

// Custom react-select components
const MenuList = (props: MenuListComponentProps<any>) => {
	return <S.MenuList {...props}>{props.children}</S.MenuList>;
};

const FundsAutocomplete = ({
	config,
	computed,
	htmlId,
	value,
	handleChange,
	getDisplayError,
	renderTextNode,
	ctrlHolderProps,
	path,
	setValues,
	formValues,
}: IProps) => {
	const [showOptions, setShowOptions] = useState<boolean>(false);
	const [fieldError, setFieldError] = useState<string | undefined>('');
	const [searchResult, setSearchResult] = useState<
		TReferenceDataSuperFundsUnion[] | null
	>(null);

	const { data = {}, appendMsg, bottomMsg } = config;
	const {
		placeholder,
		labelHelpText,
		inputAttrs = {},
		fullWidth,
		pathFundName,
		pathAbn,
		pathSpinUsi,
		usiSearch,
		isATOFundSearch,
		fundAbnErrMessage,
	} = data;

	const label = isJssField(data.label)
		? data.label
		: renderTextNode(data.label, 'span');
	const helpText = isJssField(labelHelpText)
		? labelHelpText
		: renderTextNode(labelHelpText, 'span');
	const tooltip = renderTextNode(data.tooltip);
	const isUsiSearch = typeof usiSearch === 'boolean' ? usiSearch : false;

	const [isApiRunning, setIsApiRunning] = useState<boolean>(false);

	const errorMsg = getDisplayError();
	const hasError = !!fieldError || !!errorMsg;
	const { required, disabled } = computed;
	const _ctrlHolderProps = {
		htmlId,
		label,
		helpText,
		required,
		appendMsg,
		value,
		disabled,
		bottomMsg,
		placeholder,
		type: inputAttrs?.type,
		fullWidth: data.fullWidth,
		errorMsg: fieldError ?? errorMsg,
		...(data.tooltip && { tooltip }),
		...ctrlHolderProps,
	};

	const pathFundAutocomplete = path.split('.')[0];

	const setFormikValues = (path: string, value: string) => {
		// IMPORTANT: Set Formik values to reflect on formValues
		set(formValues, path, value);
		// IMPORTANT: Do not remove. This counters the delay.
		setTimeout(() => {
			setValues(formValues);
		});
	};

	// TODO: Add this in the JSS Props and make the AutoComplete component call it from there.
	const ER103 =
		fundAbnErrMessage || 'The ABN should have 11 numbers with no spaces...';

	const onInputChange = (inputText: string) => {
		// =========PLACEHOLDER=========== //

		/***
		 * @description Remove all the spaces in an input
		 * This aims to allow inputs of numbers with spaces on them
		 * Ex: 12 345 678 901 -- This is a valid ABN
		 */
		const searchText = inputText.replace(/\s/g, '');

		// @note: This is a check to see if the input contains letters
		// If the input contains letters, display the ER103 error
		const hasLettersRegex = new RegExp(/[a-zA-Z]/g);
		if (!!isATOFundSearch && hasLettersRegex.test(searchText)) {
			setFieldError(ER103);
		}

		// @note: This is a check to make sure that the
		// entered ABN is not more than 11 digits
		if (!!isATOFundSearch && searchText.length > 11) {
			setFieldError(ER103);
			setShowOptions(false);
		} else {
			if (searchText?.length >= 3) {
				if (!!isATOFundSearch) {
					const regex = new RegExp(/^[0-9 ]*$/);
					if (regex.test(searchText)) {
						setFieldError(undefined);
						setShowOptions(true);
					} else {
						setFieldError(ER103);
						setShowOptions(false);
					}
				} else {
					setShowOptions(true);
				}
			} else {
				setShowOptions(false);
			}
		}
	};

	const onChange = (option: ValueType<TOption>) => {
		if (option !== MANUAL_FUND_OPTION) {
			const selectedFund = searchResult?.find(
				fund => fund?.name === (option as TOption)?.value,
			);
			// IMPORTANT: Do not remove. This counters the delay.
			setTimeout(() => {
				setFormikValues(pathFundAutocomplete, (option as TOption)?.value);
				setFormikValues(
					pathFundName,
					typeof selectedFund?.name === 'string' ? selectedFund?.name : '',
				);
				setFormikValues(
					pathAbn,
					typeof selectedFund?.abn === 'string' ? selectedFund?.abn : '',
				);
				setFormikValues(
					pathSpinUsi,
					typeof selectedFund?.usi === 'string' ? selectedFund?.usi : '',
				);
			});
		}

		// Note: Optional callback
		handleChange?.(option);
	};

	const _callFundsAPI = (
		getFundsParam: {},
		resolve: (value: unknown) => void,
	) => {
		return axios({
			url: `${referenceBase}/datasets/funds`,
			params: getFundsParam,
			method: 'GET',
			responseType: 'json',
			headers: {
				'Authorization': `Bearer ${getAccessToken()}`,
				'Accept': 'application/json',
				'Cache-Control': 'no-cache',
				'X-API-Key': apiConfig?.externalServices?.coarseGrainApiKey,
				'X-Correlation-Id': uuidv4(), // FED initiated correlationID
				'X-Correlation-App': X_CORRELATION_APP, // Link detection of request origin
			},
			withCredentials: AKAMAI_BOT_FLAG,
		})
			.then(response => {
				const result = response?.data as ReferenceDataSuperFunds;
				const resultDataValues = result?.data
					?.values as TReferenceDataSuperFundsUnion[];

				setIsApiRunning(false);
				setSearchResult(resultDataValues);

				if (Array.isArray(resultDataValues) && resultDataValues?.length > 0) {
					resolve([
						...resultDataValues?.map((fund: TReferenceDataSuperFundsUnion) => ({
							label: fund?.name,
							value: fund?.name,
						})),
						MANUAL_FUND_OPTION,
					]);
				} else {
					resolve([MANUAL_FUND_OPTION]);
				}
			})
			.catch(() => {
				resolve([MANUAL_FUND_OPTION]);
			});
	};

	const _loadAutocompleteOptions = (searchString: string) => {
		const searchText = searchString.replace(/\s/g, '');

		// @note: This is a check to see if the input contains letters
		// If the input contains letters, display the ER103 error
		const hasLettersRegex = new RegExp(/[a-zA-Z]/g);

		if (
			!!isATOFundSearch &&
			(searchText.length > 11 || hasLettersRegex.test(searchText))
		) {
			setFieldError(ER103);
		} else {
			setFieldError(undefined);
			if (searchText?.length >= 3) {
				if (isStorybook()) {
					return new Promise(resolve => {
						setTimeout(() => {
							if (searchText?.toLowerCase() === SB_FAIL) {
								// Note: Error scenario
								resolve([MANUAL_FUND_OPTION]);
							} else {
								// Note: Success scenario
								setSearchResult(
									isATOFundSearch
										? GetSMSFYesAbnFunds?.data?.values
										: isUsiSearch
										? GetSuperFundsUsi?.data?.values
										: GetSuperFundsApra?.data?.values,
								);

								resolve([
									...(isATOFundSearch
										? GetSMSFYesAbnFunds?.data?.values?.map(
												(fund: TReferenceDataSuperFundsUnion) => ({
													label: fund?.name,
													value: fund?.name,
												}),
										  )
										: isUsiSearch
										? GetSuperFundsUsi?.data?.values?.map(
												(fund: TReferenceDataSuperFundsUnion) => ({
													label: fund?.name,
													value: fund?.name,
												}),
										  )
										: GetSuperFundsApra?.data?.values?.map(
												(fund: TReferenceDataSuperFundsUnion) => ({
													label: fund?.name,
													value: fund?.name,
												}),
										  )),
									MANUAL_FUND_OPTION,
								]);
							}
						}, 1500);
					});
				} else {
					/**
					 * Note: See Notes in FundAutocomplete.md
					 */
					const strippedSearchText = searchText?.replace(/\s/g, '');
					const getFundsParam = isUsiSearch
						? { usi: strippedSearchText }
						: isNaN(Number(strippedSearchText))
						? {
								fund_name: strippedSearchText,
								fund_status: ACTIVE_FUND_STATUS,
						  }
						: isATOFundSearch
						? {
								abn: strippedSearchText,
								fund_status: ACTIVE_FUND_STATUS,
								fund_type: 'ATO',
						  }
						: { abn: strippedSearchText };

					return new Promise(resolve => {
						// NOTE: check JWT if  expired
						// if YES - call refresh token - then fund ABN API call
						// if NO - just call fund ABN API
						const isAccesTokenExpired = isJwtExpired(getAccessToken());

						if (
							isAccesTokenExpired &&
							(!isStorybook() || !isExperienceEditorActive())
						) {
							doRefreshToken()
								.then(() => {
									if (isApiRunning) {
										return;
									}
									setIsApiRunning(true);

									return _callFundsAPI(getFundsParam, resolve);
								})
								.catch(() => resolve);
						} else {
							setTimeout(async () => {
								setIsApiRunning(true);

								return _callFundsAPI(getFundsParam, resolve);
							}, 2000);
						}
					});
				}
			}
		}
	};

	return (
		<Field
			name={pathFundAutocomplete}
			render={() => {
				return (
					<UpdatedCtrlHolder {..._ctrlHolderProps}>
						<S.FundsAutocompleteContainer
							{...{ disabled, hasError, fullWidth }}
						>
							<AsyncSelect
								placeholder={placeholder}
								className="formBuilder__fundsAutocomplete"
								classNamePrefix="formBuilder__fundsAutocomplete"
								isDisabled={disabled}
								loadOptions={_loadAutocompleteOptions}
								onChange={onChange}
								onInputChange={onInputChange}
								components={{
									MenuList,
									DropdownIndicator: null,
								}}
								isClearable
								openMenuOnFocus={false}
								openMenuOnClick={false}
								noOptionsMessage={(obj: { inputValue: string }) => {
									// Note: Extra catch but should not be triggered
									// since we are always resolving a manual value on failure
									return obj?.inputValue?.length >= 3 ? MANUAL_LABEL : null;
								}}
								aria-label="Select"
								menuPortalTarget={document.body}
								menuPosition="fixed"
								menuIsOpen={showOptions}
								onBlurResetsInput={false}
								// Note: Empty array is to force "clear" dropdown input on every change
								value={[]}
								onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
									const value = e.key;
									const regex = new RegExp(/^[0-9 ]{1,9}$/);
									if (!!isATOFundSearch && !!value && value !== '') {
										if (value === 'Backspace' || e.ctrlKey) {
											return e.key;
										}

										if (!regex.test(value)) {
											e.preventDefault();
										}
									}
								}}
							/>
						</S.FundsAutocompleteContainer>
					</UpdatedCtrlHolder>
				);
			}}
		/>
	);
};

export default FundsAutocomplete;
